import { create } from 'zustand'
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { auth } from '@/config/firebase'
import type { AuthState } from '@/types/context'

interface AuthStore extends AuthState {}

const googleProvider = new GoogleAuthProvider()
const DEV_AUTH_STORAGE_KEY = 'contextmd_dev_auth'
const ONBOARDING_STORAGE_KEY = 'contextmd_onboarded'
const DEV_USER = {
  uid: 'dev-mock-user',
  email: 'dev@local.contextmd',
  displayName: 'Dev User',
  photoURL: null,
}

const hasDevAuthSession = (): boolean => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false
  return window.localStorage.getItem(DEV_AUTH_STORAGE_KEY) === 'true'
}

const setDevAuthSession = (enabled: boolean): void => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return
  if (enabled) {
    window.localStorage.setItem(DEV_AUTH_STORAGE_KEY, 'true')
  } else {
    window.localStorage.removeItem(DEV_AUTH_STORAGE_KEY)
  }
}

const initialDevUser = !auth && hasDevAuthSession() ? DEV_USER : null

export const useAuthStore = create<AuthStore>((set) => ({
  user: initialDevUser,
  loading: auth ? true : false,
  error: null,

  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      
      if (!auth) {
        throw new Error('Firebase Auth not initialized')
      }
      
      const result = await signInWithEmailAndPassword(auth, email, password)
      const user = result.user
      
      set({ 
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed'
      set({ 
        user: null,
        loading: false, 
        error: errorMessage 
      })
      throw error
    }
  },

  signUp: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null })
      
      if (!auth) {
        throw new Error('Firebase Auth not initialized')
      }
      
      const result = await createUserWithEmailAndPassword(auth, email, password)
      const user = result.user
      
      set({ 
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed'
      set({ 
        user: null,
        loading: false, 
        error: errorMessage 
      })
      throw error
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ loading: true, error: null })
      
      if (!auth) {
        if (import.meta.env.DEV) {
          setDevAuthSession(true)
          if (typeof window !== 'undefined') {
            // In local dev test mode, always re-enter onboarding after login.
            window.localStorage.removeItem(ONBOARDING_STORAGE_KEY)
          }
          set({
            user: DEV_USER,
            loading: false,
            error: null
          })
          return
        }
        throw new Error('Firebase Auth not initialized')
      }
      
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user
      
      set({ 
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google sign in failed'
      set({ 
        user: null,
        loading: false, 
        error: errorMessage 
      })
      throw error
    }
  },

  signOut: async () => {
    try {
      set({ loading: true, error: null })
      
      if (!auth) {
        if (import.meta.env.DEV) {
          setDevAuthSession(false)
          set({
            user: null,
            loading: false,
            error: null
          })
          return
        }
        throw new Error('Firebase Auth not initialized')
      }
      
      await firebaseSignOut(auth)
      
      set({ 
        user: null,
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign out failed'
      set({ 
        loading: false, 
        error: errorMessage 
      })
      throw error
    }
  },

  clearError: () => {
    set({ error: null })
  }
}))

// Set up auth state listener
if (auth) {
  onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
    const { user: currentUser } = useAuthStore.getState()
    
    if (firebaseUser) {
      const user = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL
      }
      
      // Only update if user has changed to avoid unnecessary re-renders
      if (!currentUser || currentUser.uid !== user.uid) {
        useAuthStore.setState({ 
          user,
          loading: false,
          error: null
        })
      } else {
        useAuthStore.setState({ loading: false })
      }
    } else {
      if (currentUser) {
        useAuthStore.setState({ 
          user: null,
          loading: false,
          error: null
        })
      } else {
        useAuthStore.setState({ loading: false })
      }
    }
  })
} else {
  // If Firebase Auth is not initialized (dev), restore mock session if present.
  useAuthStore.setState({
    user: hasDevAuthSession() ? DEV_USER : null,
    loading: false,
    error: null,
  })
}
